import { Subscribe } from '@react-rxjs/core'
import { graphql } from 'gatsby'
import React, { forwardRef, useMemo } from 'react'

import { MenuEntryFragmentFragment } from '../../../graphql/gatsby'
import { Maybe } from '../../../groq/sanity-dispensaries/types'
import {
  latestDispensaryIsContactFilled$,
  useDispensaryIsContactFilled,
} from '../../../lib/dispensaries/hooks/useDispensaryIsContactFilled'
import { useSanityRoleAndContextBasedContent } from '../../../lib/sanity'
import SanityLink, { SanityLinkProps, SanityLinkRef } from '../SanityLink'

type ContactRequiredMenuEntryLinkContentProps = Omit<SanityLinkProps, 'ref' | 'to'> & {
  link: MenuEntryFragmentFragment['link']
}

export const ContactRequiredMenuEntryLinkContent = forwardRef<
  SanityLinkRef,
  ContactRequiredMenuEntryLinkContentProps
>(function MenuEntryLinkContent({ link, children, ...otherProps }, ref) {
  const isContactFilled = useDispensaryIsContactFilled()

  if (!isContactFilled) {
    return null
  }

  if (link) {
    return (
      <SanityLink to={link} ref={ref} {...otherProps}>
        {children}
      </SanityLink>
    )
  }
  // return link to nowhere if link not specified
  return (
    <SanityLink to="#0" ref={ref} onClick={(e) => e.preventDefault()} {...otherProps}>
      {children}
    </SanityLink>
  )
})

export type MenuEntryLinkProps = Omit<SanityLinkProps, 'ref' | 'to'> & {
  link: MenuEntryFragmentFragment['link']
  roleBasedContent?: MenuEntryFragmentFragment['roleBasedContent']
  contextBasedContent?: MenuEntryFragmentFragment['contextBasedContent']
  dispensaryContactInformationRequired?: Maybe<boolean>
}

const MenuEntryLink = forwardRef<SanityLinkRef, MenuEntryLinkProps>(function MenuEntryLink(
  {
    link,
    contextBasedContent,
    roleBasedContent,
    children,
    dispensaryContactInformationRequired,
    ...otherProps
  },
  ref,
) {
  const { shouldDisplayContent } = useSanityRoleAndContextBasedContent()
  const shouldDisplay = useMemo(
    () => shouldDisplayContent({ contextBasedContent, roleBasedContent }),
    [contextBasedContent, roleBasedContent, shouldDisplayContent],
  )
  if (!shouldDisplay) {
    return null
  }

  if (dispensaryContactInformationRequired) {
    return (
      <Subscribe source$={latestDispensaryIsContactFilled$}>
        <ContactRequiredMenuEntryLinkContent link={link} ref={ref} {...otherProps}>
          {children}
        </ContactRequiredMenuEntryLinkContent>
      </Subscribe>
    )
  }

  if (link) {
    return (
      <SanityLink to={link} ref={ref} {...otherProps}>
        {children}
      </SanityLink>
    )
  }
  // return link to nowhere if link not specified
  return (
    <SanityLink to="#0" ref={ref} onClick={(e) => e.preventDefault()} {...otherProps}>
      {children}
    </SanityLink>
  )
})

export default MenuEntryLink

export const menuEntry = graphql`
  fragment MenuEntryContent on SanityMenuEntryContent {
    show
    contentType
    products {
      title
      products {
        sku
        title
        image {
          _rawAsset
        }
      }
    }
    staticContent {
      title
      _rawDescription
      image {
        ...SanityImageObject
      }
    }
    categories {
      title
      categories {
        title
        isActive
      }
    }
    libraryContent {
      content {
        ... on SanityCaseStudy {
          id
          __typename
          title
          shortDescription
          thumbnail {
            ...SanityImageObject
          }
          publishedDate
          slug {
            current
          }
        }
        ... on SanityCasiSession {
          id
          __typename
          title
          shortDescription
          thumbnail {
            ...SanityImageObject
          }
          publishedDate
          slug {
            current
          }
        }
        ... on SanityCatalogTraining {
          id
          __typename
          title
          shortDescription
          thumbnail {
            ...SanityImageObject
          }
          publishedDate
          slug {
            current
          }
        }
        ... on SanityCategoryBrochure {
          id
          __typename
          title
          shortDescription
          thumbnail {
            ...SanityImageObject
          }
          publishedDate
          slug {
            current
          }
        }
        ... on SanityJournalArticle {
          id
          __typename
          title
          shortDescription
          thumbnail {
            ...SanityImageObject
          }
          publishedDate
          slug {
            current
          }
        }
        ... on SanityLiteratureReference {
          id
          __typename
          title
          shortDescription
          thumbnail {
            ...SanityImageObject
          }
          publishedDate
          slug {
            current
          }
        }
        ... on SanityPatientBrochure {
          id
          __typename
          title
          shortDescription
          thumbnail {
            ...SanityImageObject
          }
          publishedDate
          slug {
            current
          }
        }
        ... on SanityPatientEducation {
          id
          __typename
          title
          shortDescription
          thumbnail {
            ...SanityImageObject
          }
          publishedDate
          slug {
            current
          }
        }
        ... on SanityPriceList {
          id
          __typename
          title
          shortDescription
          thumbnail {
            ...SanityImageObject
          }
          publishedDate
          slug {
            current
          }
        }
        ... on SanityPrivateLabelForm {
          id
          __typename
          title
          shortDescription
          thumbnail {
            ...SanityImageObject
          }
          publishedDate
          slug {
            current
          }
        }
        ... on SanityProductReferenceChart {
          id
          __typename
          title
          shortDescription
          thumbnail {
            ...SanityImageObject
          }
          publishedDate
          slug {
            current
          }
        }
        ... on SanityProtocol {
          id
          __typename
          title
          shortDescription
          thumbnail {
            ...SanityImageObject
          }
          publishedDate
          slug {
            current
          }
        }
        ... on SanitySocialMediaResource {
          id
          __typename
          title
          shortDescription
          publishedDate
          slug {
            current
          }
          asset {
            _type
            ...SanityImageObject
          }
        }
        ... on SanityTechSheet {
          id
          __typename
          title
          shortDescription
          thumbnail {
            ...SanityImageObject
          }
          publishedDate
          slug {
            current
          }
        }
        ... on SanityTechSheetFrench {
          id
          __typename
          title
          shortDescription
          thumbnail {
            ...SanityImageObject
          }
          publishedDate
          slug {
            current
          }
        }
        ... on SanityWebinar {
          id
          __typename
          title
          shortDescription
          thumbnail {
            ...SanityImageObject
          }
          publishedDate
          slug {
            current
          }
        }
        ... on SanityWhitePaper {
          id
          __typename
          title
          shortDescription
          thumbnail {
            ...SanityImageObject
          }
          publishedDate
          slug {
            current
          }
        }
      }
    }
  }
  fragment MenuEntryFragment on SanityMenuEntry {
    _id
    name
    newColumn
    dispensaryContactInformationRequired
    content {
      ...MenuEntryContent
    }
    roleBasedContent {
      ...RoleBasedContentFragment
    }
    contextBasedContent {
      ...ContextBasedContentFragment
    }
    link {
      ...LinkFragment
    }
  }
`
